import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';

/**
 * Convert an object of observables into an observable object
 * @param {any} state - The object of observables to to converted, eg: {a$: Observable, b$: Observable}
 * @param {string} symbol *optional - the symbol appended to the end of the observables in the state param
 * @return {Observable} An observable object containing all of the mapped properties of the state param.
 * In component
 * a$ = new Observable()
 * b$ = new Observable()
 * state$ = getStateAsObservable(this)
 * In template
 * *ngIf = "(state$ | async) as state" {{state.a}} {{state.b}}
 *
 * Function will filter observables with '$' appended to the end by default or symbol param if specified
 */

export function getStateAsObservable(
  state: any,
  symbol?: string,
): Observable<any> {
  /**
   * @param  {object} state$
   * @returns string
   */
  const filterObservableProperties = (state$: object): string[] =>
    Object.keys(state$).filter((key) => key.endsWith(symbol || '$'));

  const observables = filterObservableProperties({ ...state });

  /**
   * @param  {object} state$
   * @returns Observable
   */
  const mapObservables = (state$: any): Observable<any>[] => {
    /* Using startWith(undefined) to emit initial values for observables that have no initial value.
        This is so combineLatest will always emit a value. */
    const mappedObservables = [...observables].map((observable) =>
      state$[observable]?.pipe(startWith(undefined)),
    );

    return mappedObservables;
  };

  /**
   * @param  {object} mappedState
   * @returns any
   */
  const mapStatePropertiesToObservable =
    (mappedState: any): any =>
    (acc: any, curr: string, index: string | number) => ({
      ...acc,
      [curr.replace(symbol || /\$/, '')]: mappedState[index],
    });

  /**
   * @param  {string[]} mappedProperties
   * @param  {object} mappedState
   * @returns object
   */
  const mapStateToObservables = (
    mappedProperties: string[],
    mappedState: object,
  ): object =>
    mappedProperties.reduce(mapStatePropertiesToObservable(mappedState), {});

  /**
   * @param  {[object]} [...state$]
   * @returns object
   */
  const mapState = ([...state$]: [object]): object =>
    /**
     * uncomment below to see resolved state.
     * Remember it must be used as observable in template with async pipe
     * EG: *ngIf="state$ | async as state"
     */
    // console.log(`STATEMAP$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$`, stateMap)
    mapStateToObservables(observables, state$);
  /**
   * @param  {any} error
   * @returns Observable
   */
  const catchStateError = (error: any): Observable<any> => of(error);

  const state$$ = combineLatest(mapObservables(state)).pipe(
    <any>map(mapState),
    catchError(catchStateError),
  );

  return state$$;
}
